import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Languages, Loader2, Check, X, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback } from './ui/avatar';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

// Displays aggregate stars, star-based distribution, review list with auto-translation toggle,
// and (for verified buyers) an inline review form.
export default function ProductReviews({ productId, isOwner, purchased }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [hasMyReview, setHasMyReview] = useState(false);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const viewerLang = (i18n.language || 'en').split('-')[0];
      const res = await api.get(`/products/${productId}/reviews`, { params: { lang: viewerLang } });
      setReviews(res.data.reviews || []);
      setStats(res.data.stats || { count: 0, average: 0 });
      if (user) {
        const mine = (res.data.reviews || []).find(r => r.buyer_id === user.id);
        setHasMyReview(!!mine);
        if (mine) {
          setRating(mine.rating || 5);
          setText(mine.text || '');
        }
      }
    } catch (_) { /* silent */ } finally {
      setLoading(false);
    }
  }, [productId, i18n.language, user]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) {
      toast.error(t('reviews.text_required') !== 'reviews.text_required' ? t('reviews.text_required') : 'Please write a short review');
      return;
    }
    setSubmitting(true);
    try {
      const sourceLang = (i18n.language || 'en').split('-')[0];
      await api.post(`/products/${productId}/reviews`, { rating, text: text.trim(), source_lang: sourceLang });
      toast.success(t('reviews.submitted') !== 'reviews.submitted' ? t('reviews.submitted') : 'Review submitted');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || (t('reviews.submit_failed') !== 'reviews.submit_failed' ? t('reviews.submit_failed') : 'Failed to submit review');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const removeReview = async (rid) => {
    try {
      await api.delete(`/reviews/${rid}`);
      await load();
      toast.success(t('reviews.deleted') !== 'reviews.deleted' ? t('reviews.deleted') : 'Review removed');
    } catch {
      toast.error('Could not remove review');
    }
  };

  const showForm = !!user && !isOwner && purchased;

  return (
    <section className="mt-10 border-t border-amber-100 pt-8" data-testid="product-reviews">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="font-serif text-xl md:text-2xl text-obsidian">
            {t('reviews.title') !== 'reviews.title' ? t('reviews.title') : 'Buyer Reviews'}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {t('reviews.multilingual_hint') !== 'reviews.multilingual_hint' ? t('reviews.multilingual_hint') : 'Reviews auto-translate to your language'}
          </p>
        </div>
        {stats.count > 0 && (
          <div className="flex items-center gap-2" data-testid="reviews-aggregate">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={16} className={i <= Math.round(stats.average) ? 'fill-amber-400 text-amber-400' : 'text-stone-300'} />
              ))}
            </div>
            <span className="text-sm font-medium text-obsidian">{stats.average.toFixed(1)}</span>
            <span className="text-xs text-muted-foreground">({stats.count})</span>
          </div>
        )}
      </div>

      {/* Inline review form for verified buyers */}
      {showForm && (
        <div className="mb-8 p-4 rounded-lg border border-amber-200 bg-amber-50/30" data-testid="review-form">
          <p className="text-xs uppercase tracking-[0.15em] text-amber-700 font-semibold mb-3">
            {hasMyReview
              ? (t('reviews.edit_yours') !== 'reviews.edit_yours' ? t('reviews.edit_yours') : 'Edit your review')
              : (t('reviews.write_yours') !== 'reviews.write_yours' ? t('reviews.write_yours') : 'Share your experience')}
          </p>
          <div className="flex items-center gap-1 mb-3">
            {[1,2,3,4,5].map(i => (
              <button
                key={i}
                type="button"
                data-testid={`review-star-${i}`}
                onClick={() => setRating(i)}
                className="p-0.5 hover:scale-110 transition-transform"
                aria-label={`${i} stars`}
              >
                <Star size={22} className={i <= rating ? 'fill-amber-400 text-amber-400' : 'text-stone-300'} />
              </button>
            ))}
          </div>
          <Textarea
            data-testid="review-text"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('reviews.placeholder') !== 'reviews.placeholder' ? t('reviews.placeholder') : 'Write your honest thoughts in your language — buyers worldwide will read it in theirs.'}
            className="min-h-[90px] text-sm border-amber-200/80 resize-none"
            maxLength={2000}
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-[10px] text-muted-foreground">{text.length}/2000</span>
            <Button
              data-testid="review-submit-btn"
              onClick={submit}
              disabled={submitting || !text.trim()}
              className="bg-obsidian text-white hover:bg-amber-600 h-9 px-5 text-xs rounded-sm"
            >
              {submitting ? <Loader2 size={12} className="animate-spin mr-2" /> : <Check size={12} className="mr-2" />}
              {hasMyReview
                ? (t('reviews.update') !== 'reviews.update' ? t('reviews.update') : 'Update review')
                : (t('reviews.submit') !== 'reviews.submit' ? t('reviews.submit') : 'Post review')}
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-amber-500" /></div>
      ) : reviews.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="reviews-empty">
          {t('reviews.empty') !== 'reviews.empty' ? t('reviews.empty') : 'No reviews yet. Be the first to share your experience.'}
        </div>
      ) : (
        <ul className="space-y-4">
          {reviews.map(r => (
            <ReviewItem key={r.id} r={r} t={t} currentUser={user} onDelete={removeReview} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ReviewItem({ r, t, currentUser, onDelete }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const hasTranslation = !!r.translated_text && r.translated_text !== r.text;
  const display = showOriginal || !hasTranslation ? r.text : r.translated_text;
  const canDelete = currentUser && (currentUser.id === r.buyer_id || currentUser.role === 'admin');
  return (
    <li className="p-4 rounded-lg border border-stone-100 bg-white" data-testid={`review-${r.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 bg-amber-100">
            <AvatarFallback className="text-xs text-amber-700 font-semibold">
              {(r.buyer_name || '?').slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium text-obsidian">{r.buyer_name}</p>
            <div className="flex items-center gap-0.5 mt-0.5">
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={11} className={i <= (r.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-stone-300'} />
              ))}
              <span className="text-[10px] text-muted-foreground ml-2">
                {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
              </span>
            </div>
          </div>
        </div>
        {canDelete && (
          <button
            onClick={() => onDelete(r.id)}
            data-testid={`review-delete-${r.id}`}
            className="text-stone-300 hover:text-red-500 p-1"
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
      {hasTranslation && !showOriginal && (
        <div className="inline-flex items-center gap-1.5 text-[10px] text-amber-600 mb-1.5">
          <Languages size={10} />
          <span>{t('reviews.translated_from') !== 'reviews.translated_from' ? t('reviews.translated_from', { lang: (r.original_language || '').toUpperCase() }) : `Translated from ${(r.original_language || '').toUpperCase()}`}</span>
        </div>
      )}
      <p className="text-sm text-obsidian/80 leading-relaxed whitespace-pre-wrap">{display}</p>
      {hasTranslation && (
        <button
          onClick={() => setShowOriginal(v => !v)}
          data-testid={`review-${r.id}-toggle`}
          className="mt-2 text-[10px] underline text-amber-600 hover:text-amber-700"
        >
          {showOriginal
            ? (t('reviews.show_translation') !== 'reviews.show_translation' ? t('reviews.show_translation') : 'Show translation')
            : (t('reviews.show_original') !== 'reviews.show_original' ? t('reviews.show_original') : 'Show original')}
        </button>
      )}
    </li>
  );
}
